import { SiteShell, useSite } from "@/components/site/SiteShell";
import { Btn, CtaBanner, Eyebrow, PageHero } from "@/components/site/Blocks";
import { ArrowLink, Marquee, Rule } from "@/components/site/Motion";
import { SITE_IMG, SERVICES, STEPS, serviceImg } from "@/lib/siteImages";
import { siteImg, usePageHead } from "@/lib/siteContent";
import { useReveal } from "@/lib/useReveal";

const EXTRAS = [
  "Digital templating once your cabinets are set",
  "Vein-matched seams, waterfalls and mitred edges",
  "Sink, cook-top and faucet cut-outs in-house",
  "Tear-out and disposal of your old tops",
  "Remnant offcuts for vanities, hearths and shelves",
];

export default function SiteServices() {
  const site = useSite();
  const cfg = site?.config || {};
  const shop = site?.shop || {};
  usePageHead({
    title: "Stone fabrication & installation services",
    description:
      "Digital templating, precision CNC fabrication and professional installation of granite, quartz, quartzite and porcelain countertops across St. Louis.",
    suffix: shop?.name,
  });
  const svc = cfg.services || {};
  const cards = svc.items?.length
    ? svc.items
    : SERVICES.map((s) => ({ title: s.name, body: s.blurb, image: "" }));
  const included = svc.included?.length ? svc.included : EXTRAS;
  const steps = cfg.workflow?.items?.length ? cfg.workflow.items : STEPS;
  useReveal([site]);

  return (
    <SiteShell site={site} testId="site-services" overHero>
      <PageHero
        testId="services-hero"
        image={siteImg(cfg.workflow?.image, SITE_IMG.fabrication)}
        eyebrow={svc.micro || "Services"}
        title={svc.heading || "Cut, polished and set by our own crew"}
        body={svc.sub}
      >
        <div className="mt-10">
          <Btn to="/contact" testId="services-hero-quote">
            Get a free quote
          </Btn>
        </div>
      </PageHero>

      {/* Ticker of everything we build */}
      <div className="border-b border-[color:var(--s-line)] bg-[color:var(--s-surface)] py-5">
        <Marquee speed={32} testId="services-ticker">
          {cards.map((s, i) => (
            <span key={i} className="s-tag">
              {s.title}
            </span>
          ))}
        </Marquee>
      </div>

      {/* Service rows */}
      <section className="bg-[color:var(--s-bg)] pt-24 sm:pt-32">
        <div className="s-container">
          <div className="s-reveal">
            <div className="s-index mb-5" data-testid="services-index-1"><b>(01)</b> What we do</div>
            <h2 className="s-h mt-2 break-words text-4xl uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl" data-testid="services-heading">
              {svc.page_heading || "Services."}
            </h2>
          </div>
          <Rule className="mt-12" />
          <div className="pb-8">
            {cards.map((s, i) => (
              <article
                key={s.title}
                data-testid={`service-card-${(s.title || "").split(" ")[0].toLowerCase()}`}
                className="s-reveal grid gap-8 border-b border-[color:var(--s-line)] py-12 lg:grid-cols-[4rem_1fr_1fr] lg:items-center lg:gap-14 lg:py-16"
              >
                <span className="s-num text-sm text-[color:var(--s-gold)]">{String(i + 1).padStart(2, "0")}</span>
                <div className={i % 2 ? "lg:order-3" : ""}>
                  <h3 className="s-hx text-3xl sm:text-4xl lg:text-5xl">
                    <strong>{s.title}</strong>
                  </h3>
                  <p className="mt-6 max-w-md text-base leading-relaxed text-[color:var(--s-mute)]">{s.body}</p>
                  <ArrowLink to="/contact" className="mt-8" testId={`service-link-${i}`}>
                    Price this job
                  </ArrowLink>
                </div>
                <div className={`s-fig s-soft aspect-[4/3] ${i % 2 ? "lg:order-2" : ""}`}>
                  <img src={siteImg(s.image, serviceImg(s.title))} alt={s.title} className="h-full w-full object-cover" />
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Included + process */}
      <section className="bg-[color:var(--s-cream)] py-24 sm:py-32">
        <div className="s-container grid gap-16 lg:grid-cols-2 lg:gap-24">
          <div className="s-reveal">
            <div className="s-index mb-5" data-testid="services-index-2"><b>(02)</b> Included as standard</div>
            <h2 className="s-h mt-2 max-w-md text-4xl uppercase leading-[0.95] sm:text-5xl">
              {svc.included_heading || "No surprise line items."}
            </h2>
            <ul className="mt-10" data-testid="services-included">
              {included.map((e, i) => (
                <li key={e} className="flex items-baseline gap-5 border-t border-[color:var(--s-ink)]/10 py-4 text-base">
                  <span className="s-num text-sm text-[color:var(--s-gold)]">{String(i + 1).padStart(2, "0")}</span>
                  {e}
                </li>
              ))}
            </ul>
            <ArrowLink to="/contact" className="mt-10" testId="services-quote">
              Get a free quote
            </ArrowLink>
          </div>
          <div className="s-reveal" data-delay="1">
            <div className="s-index mb-5" data-testid="services-index-3"><b>(03)</b> {cfg.workflow?.micro || "How it works"}</div>
            <h2 className="s-h mt-2 text-4xl uppercase leading-[0.95] sm:text-5xl">{cfg.workflow?.heading || "One shop, start to finish."}</h2>
            <ol className="mt-8 grid gap-x-8 sm:grid-cols-2" data-testid="services-steps">
              {steps.slice(0, 6).map((s, i) => (
                <li key={s.title} className="border-t border-[color:var(--s-ink)]/10 py-6">
                  <span className="s-num text-sm text-[color:var(--s-gold)]">{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="mt-3 text-lg font-semibold">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[color:var(--s-mute)]">{s.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <CtaBanner cfg={cfg.cta} shop={shop} image={siteImg(cfg.cta?.image || cfg.why?.image, SITE_IMG.kitchenDark)} testId="services-cta" />
    </SiteShell>
  );
}
